// --- src/components/PricingInput.tsx ---
import React, { useState, useEffect, useCallback, useRef } from 'react';

export interface PricingInputProps {
  // Purchase pricing
  purchaseNet?: number;
  purchaseGross?: number;
  onPurchaseNetChange?: (value: number) => void;
  onPurchaseGrossChange?: (value: number) => void;

  // Selling pricing
  sellingNet?: number;
  sellingGross?: number;
  onSellingNetChange?: (value: number) => void;
  onSellingGrossChange?: (value: number) => void;

  // VAT rate
  vatRate?: number;
  onVatRateChange?: (value: number) => void;

  // Labels (optional, for customization)
  purchaseLabel?: string;
  sellingLabel?: string;
}

const VAT_RATES = [0, 5, 18, 27] as const;

/**
 * PricingInput Component
 *
 * Handles bidirectional Net <-> Gross calculation with VAT support.
 *
 * Rounding Rules:
 * - All prices (net & gross): 2 decimal places precision
 *   Sub-1 Ft items (e.g. washers at 0.15 Ft) require fractional gross prices.
 *
 * VAT Change Behavior:
 * - When VAT rate changes, Net price remains constant, Gross is recalculated
 */
export const PricingInput: React.FC<PricingInputProps> = ({
  purchaseNet,
  purchaseGross,
  onPurchaseNetChange,
  onPurchaseGrossChange,
  sellingNet,
  sellingGross,
  onSellingNetChange,
  onSellingGrossChange,
  vatRate = 27,
  onVatRateChange,
  purchaseLabel = 'Beszerzési Ár',
  sellingLabel = 'Eladási Ár',
}) => {
  // Internal state to track which field was last edited (to prevent circular updates)
  const [lastEdited, setLastEdited] = useState<
    'purchaseNet' | 'purchaseGross' | 'sellingNet' | 'sellingGross' | 'vatRate' | null
  >(null);

  // Internal state for input values (to prevent cursor jumping)
  const [purchaseNetInput, setPurchaseNetInput] = useState<string>('');
  const [sellingNetInput, setSellingNetInput] = useState<string>('');
  // Internal state for gross inputs (to prevent value override when user types)
  const [purchaseGrossInput, setPurchaseGrossInput] = useState<string>('');
  const [sellingGrossInput, setSellingGrossInput] = useState<string>('');

  // Refs to track if inputs are focused
  const purchaseNetRef = useRef<HTMLInputElement>(null);
  const sellingNetRef = useRef<HTMLInputElement>(null);
  const purchaseGrossRef = useRef<HTMLInputElement>(null);
  const sellingGrossRef = useRef<HTMLInputElement>(null);
  const roundToInt = useCallback((value: number): number => Math.round(value), []);

  // Sync internal state with props when not focused
  useEffect(() => {
    if (document.activeElement !== purchaseNetRef.current) {
      setPurchaseNetInput(
        purchaseNet !== undefined && purchaseNet > 0 ? roundToInt(purchaseNet).toString() : ''
      );
    }
  }, [purchaseNet, roundToInt]);

  useEffect(() => {
    if (document.activeElement !== sellingNetRef.current) {
      setSellingNetInput(sellingNet !== undefined && sellingNet > 0 ? sellingNet.toFixed(2) : '');
    }
  }, [sellingNet]);

  // Sync gross inputs with props when not focused
  useEffect(() => {
    if (document.activeElement !== purchaseGrossRef.current) {
      setPurchaseGrossInput(
        purchaseGross !== undefined && purchaseGross > 0 ? purchaseGross.toFixed(2) : ''
      );
    }
  }, [purchaseGross]);

  useEffect(() => {
    if (document.activeElement !== sellingGrossRef.current) {
      setSellingGrossInput(
        sellingGross !== undefined && sellingGross > 0 ? roundToInt(sellingGross).toString() : ''
      );
    }
  }, [sellingGross, roundToInt]);

  /**
   * Calculate Gross from Net: Gross = Net * (1 + VAT/100)
   * Result is rounded to 2 decimal places to support sub-1 Ft prices.
   */
  const calculateGross = useCallback((net: number, vat: number): number => {
    if (!net || net <= 0) return 0;
    return Math.round(net * (1 + vat / 100) * 100) / 100;
  }, []);

  /**
   * Calculate Net from Gross: Net = Gross / (1 + VAT/100)
   * Result is rounded to 2 decimal places
   */
  const calculateNet = useCallback((gross: number, vat: number): number => {
    if (!gross || gross <= 0) return 0;
    return Math.round((gross / (1 + vat / 100)) * 100) / 100;
  }, []);

  // Handle VAT rate change: Keep Net constant, recalculate Gross
  useEffect(() => {
    if (lastEdited === 'vatRate') {
      // Recalculate purchase Gross from Net
      if (purchaseNet !== undefined && purchaseNet > 0 && onPurchaseGrossChange) {
        const newGross = calculateGross(purchaseNet, vatRate);
        onPurchaseGrossChange(newGross);
      }

      // Recalculate selling Gross from Net
      if (sellingNet !== undefined && sellingNet > 0 && onSellingGrossChange) {
        const newGross = calculateGross(sellingNet, vatRate);
        onSellingGrossChange(newGross);
      }
    }
  }, [
    vatRate,
    purchaseNet,
    sellingNet,
    calculateGross,
    onPurchaseGrossChange,
    onSellingGrossChange,
    lastEdited,
  ]);

  // Handle purchase Net input change
  const handlePurchaseNetChange = (value: string) => {
    // Update internal state immediately (allows typing)
    setPurchaseNetInput(value);

    const numValue = parseFloat(value) || 0;
    const roundedNet = roundToInt(numValue);
    setLastEdited('purchaseNet');
    if (onPurchaseNetChange) {
      onPurchaseNetChange(roundedNet);
    }
    // Calculate Gross from Net
    if (onPurchaseGrossChange) {
      const gross = calculateGross(roundedNet, vatRate);
      onPurchaseGrossChange(gross);
    }
  };

  // Handle purchase Net blur (format on blur)
  const handlePurchaseNetBlur = () => {
    const numValue = parseFloat(purchaseNetInput) || 0;
    if (numValue > 0) {
      setPurchaseNetInput(roundToInt(numValue).toString());
    } else {
      setPurchaseNetInput('');
    }
  };

  // Handle purchase Gross input change
  const handlePurchaseGrossChange = (value: string) => {
    // Update internal state immediately (allows typing)
    setPurchaseGrossInput(value);

    const numValue = parseFloat(value) || 0;
    setLastEdited('purchaseGross');
    if (onPurchaseGrossChange) {
      onPurchaseGrossChange(Math.round(numValue * 100) / 100);
    }
    // Calculate Net from Gross
    if (onPurchaseNetChange) {
      const net = calculateNet(numValue, vatRate);
      onPurchaseNetChange(roundToInt(net));
    }
  };

  // Handle purchase Gross blur (format on blur)
  const handlePurchaseGrossBlur = () => {
    const numValue = parseFloat(purchaseGrossInput) || 0;
    if (numValue > 0) {
      setPurchaseGrossInput(numValue.toFixed(2));
    } else {
      setPurchaseGrossInput('');
    }
  };

  // Handle selling Net input change
  const handleSellingNetChange = (value: string) => {
    // Update internal state immediately (allows typing)
    setSellingNetInput(value);

    const numValue = parseFloat(value) || 0;
    setLastEdited('sellingNet');
    if (onSellingNetChange) {
      onSellingNetChange(numValue);
    }
    // Calculate Gross from Net
    if (onSellingGrossChange) {
      const gross = roundToInt(calculateGross(numValue, vatRate));
      onSellingGrossChange(gross);
    }
  };

  // Handle selling Net blur (format on blur)
  const handleSellingNetBlur = () => {
    const numValue = parseFloat(sellingNetInput) || 0;
    if (numValue > 0) {
      setSellingNetInput(numValue.toFixed(2));
    } else {
      setSellingNetInput('');
    }
  };

  // Handle selling Gross input change
  const handleSellingGrossChange = (value: string) => {
    // Update internal state immediately (allows typing)
    setSellingGrossInput(value);

    const numValue = parseFloat(value) || 0;
    const roundedGross = roundToInt(numValue);
    setLastEdited('sellingGross');
    if (onSellingGrossChange) {
      onSellingGrossChange(roundedGross);
    }
    // Calculate Net from Gross
    if (onSellingNetChange) {
      const net = calculateNet(roundedGross, vatRate);
      onSellingNetChange(net);
    }
  };

  // Handle selling Gross blur (format on blur)
  const handleSellingGrossBlur = () => {
    const numValue = parseFloat(sellingGrossInput) || 0;
    if (numValue > 0) {
      setSellingGrossInput(roundToInt(numValue).toString());
    } else {
      setSellingGrossInput('');
    }
  };

  // Handle VAT rate change
  const handleVatRateChange = (value: string) => {
    const numValue = parseInt(value, 10) || 27;
    setLastEdited('vatRate');
    if (onVatRateChange) {
      onVatRateChange(numValue);
    }
  };

  return (
    <div className="space-y-4">
      {/* VAT Rate Selector */}
      <div>
        <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-1">
          ÁFA Kulcs (%)
        </label>
        <select
          value={vatRate}
          onChange={(e) => handleVatRateChange(e.target.value)}
          className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {VAT_RATES.map((rate) => (
            <option key={rate} value={rate}>
              {rate}%
            </option>
          ))}
        </select>
      </div>

      {/* Purchase Price Section */}
      <div className="bg-slate-50 dark:bg-gray-800/50 p-4 rounded-lg border border-slate-200 dark:border-gray-700">
        <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-3">
          {purchaseLabel}
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-600 dark:text-gray-400 mb-1">
              Nettó (Ft)
            </label>
            <input
              ref={purchaseNetRef}
              type="number"
              step="1"
              min="0"
              value={purchaseNetInput}
              onChange={(e) => handlePurchaseNetChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={handlePurchaseNetBlur}
              placeholder="0"
              className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-end">
            <div className="w-full text-center text-sm text-slate-500 dark:text-gray-400 font-medium">
              × (1 + {vatRate}%)
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-600 dark:text-gray-400 mb-1">
              Bruttó (Ft)
            </label>
            <input
              ref={purchaseGrossRef}
              type="number"
              step="0.01"
              min="0"
              value={purchaseGrossInput}
              onChange={(e) => handlePurchaseGrossChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={handlePurchaseGrossBlur}
              placeholder="0.00"
              className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Selling Price Section */}
      <div className="bg-slate-50 dark:bg-gray-800/50 p-4 rounded-lg border border-slate-200 dark:border-gray-700">
        <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-3">
          {sellingLabel}
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-600 dark:text-gray-400 mb-1">
              Nettó (Ft)
            </label>
            <input
              ref={sellingNetRef}
              type="number"
              step="0.01"
              min="0"
              value={sellingNetInput}
              onChange={(e) => handleSellingNetChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={handleSellingNetBlur}
              placeholder="0.00"
              className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-end">
            <div className="w-full text-center text-sm text-slate-500 dark:text-gray-400 font-medium">
              × (1 + {vatRate}%)
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-600 dark:text-gray-400 mb-1">
              Bruttó (Ft)
            </label>
            <input
              ref={sellingGrossRef}
              type="number"
              step="1"
              min="0"
              value={sellingGrossInput}
              onChange={(e) => handleSellingGrossChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={handleSellingGrossBlur}
              placeholder="0"
              className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
